// frontend/modules/service-metrics/components/ServiceHealthCard.tsx
import React from 'react';
import { Card, Tag, Statistic, Row, Col, Progress, Typography, Space } from 'antd';
import { 
    CheckCircleOutlined, 
    WarningOutlined, 
    CloseCircleOutlined,
    QuestionCircleOutlined,
    ThunderboltOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import type { ServiceHealthDTO } from '@/shared/api/model';
import type { HealthStatus } from '../types/service-metrics.types';

const { Text } = Typography;

interface ServiceHealthCardProps {
    health: ServiceHealthDTO | null;
    loading?: boolean;
}

const getStatusConfig = (status: HealthStatus) => {
    switch (status) {
        case 'HEALTHY':
            return { color: 'success', icon: <CheckCircleOutlined />, text: 'Healthy' };
        case 'DEGRADED':
            return { color: 'warning', icon: <WarningOutlined />, text: 'Degraded' };
        case 'UNHEALTHY':
            return { color: 'error', icon: <CloseCircleOutlined />, text: 'Unhealthy' };
        case 'CRITICAL':
            return { color: 'error', icon: <ThunderboltOutlined />, text: 'Critical' };
        default:
            return { color: 'default', icon: <QuestionCircleOutlined />, text: 'Unknown' };
    }
};

const ServiceHealthCard: React.FC<ServiceHealthCardProps> = ({ health, loading }) => {
    const { dark } = useTheme();

    if (!health) {
        return (
            <Card 
                title="Service Health" 
                loading={loading}
                className={dark ? 'bg-gray-800' : ''}
            >
                <Text type="secondary">Select a service to view health status</Text>
            </Card>
        );
    }

    const statusConfig = getStatusConfig((health.status as HealthStatus) || 'UNKNOWN');
    const successRate = health.successRate ?? 0;
    const successRateColor = successRate >= 95 ? '#52c41a' : 
                            successRate >= 80 ? '#faad14' : '#ff4d4f';

    return (
        <Card 
            title={
                <Space>
                    <span>Service Health</span>
                    <Tag color={statusConfig.color} icon={statusConfig.icon}>
                        {statusConfig.text}
                    </Tag>
                </Space>
            }
            loading={loading}
            className={dark ? 'bg-gray-800' : ''}
        >
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Statistic
                        title="Success Rate"
                        value={successRate}
                        precision={2}
                        suffix="%"
                        valueStyle={{ color: successRateColor }}
                    />
                    <Progress 
                        percent={successRate} 
                        strokeColor={successRateColor}
                        showInfo={false}
                        size="small"
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title="Avg Response Time"
                        value={health.averageResponseTimeMs}
                        precision={0}
                        suffix="ms"
                        prefix={<ClockCircleOutlined />}
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title="Requests (Last Hour)"
                        value={health.requestsInLastHour}
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title="Errors (Last Hour)"
                        value={health.errorsInLastHour}
                        valueStyle={{ color: (health.errorsInLastHour ?? 0) > 0 ? '#ff4d4f' : undefined }}
                    />
                </Col>
            </Row>
            
            <div style={{ marginTop: 16 }}>
                <Space>
                    {health.circuitBreakerOpen && (
                        <Tag color="error" icon={<ThunderboltOutlined />}>
                            Circuit Breaker Open
                        </Tag>
                    )}
                    {health.rateLimitActive && (
                        <Tag color="warning" icon={<WarningOutlined />}>
                            Rate Limit Active
                        </Tag>
                    )}
                </Space>
            </div>
            
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {health.message}
            </Text>
        </Card>
    );
};

export default ServiceHealthCard;
